<template>
  <AppDialog
    v-model:open="open"
    :title="title"
    :description="description"
    :scroll-body="step === ATTENDANCE_STEP.DATES"
    :description-class="{ 'tw:-mt-0.5': step === ATTENDANCE_STEP.DATES }"
  >
    <template #title
      ><span
        class="attendance-title tw:block tw:tracking-normal"
        :class="{ 'attendance-title--compact': gap || step !== ATTENDANCE_STEP.QUESTION }"
        >{{ title }}</span
      ></template
    >
    <template v-if="gap && step === ATTENDANCE_STEP.QUESTION" #description
      ><I18nT keypath="nkf.promptDetails.gapDescription" scope="global" tag="span"
        ><template #days
          ><b>{{ attendance.gapDays }}</b></template
        ></I18nT
      ></template
    >
    <template v-if="step !== ATTENDANCE_STEP.DATES" #illustration
      ><AppDialogIllustration
        v-if="step === ATTENDANCE_STEP.QUESTION"
        :src="gap ? '/images/nkf/gap.webp' : '/images/nkf/attend.webp'"
        background="var(--card)"
        ><span
          v-if="!gap"
          class="attendance-day tw:absolute tw:grid tw:place-items-center tw:type-metric tw:text-(--info-ink) tw:tracking-normal"
          >{{ dayjs.utc(attendance.today).locale(locale).format('ddd') }}</span
        ></AppDialogIllustration
      >
      <span
        v-else-if="step === ATTENDANCE_STEP.REASON"
        class="attendance-risk icon-well tw:bg-(--destructive-soft) tw:text-(--result-high) tw:mt-6.5 tw:mx-auto tw:-mb-0.5 tw:w-14.5 tw:h-14.5 tw:rounded-(--radius-note)"
        ><AppIcon size="dialog" name="alert" /></span
    ></template>
    <AppDialogNote v-if="step === ATTENDANCE_STEP.QUESTION && gap" icon="alert" tone="danger">{{
      t('nkf.promptDetails.attendanceAdvice')
    }}</AppDialogNote>
    <FieldGroup v-if="step === ATTENDANCE_STEP.DATES" class="attendance-dates tw:gap-2.25">
      <Field
        v-for="date in dates"
        :key="date.format('YYYY-MM-DD')"
        orientation="horizontal"
        class="attendance-date item-card tw:gap-3.25 tw:p-(--card-row-padding)"
        ><Checkbox
          :id="'att-' + date.format('YYYY-MM-DD')"
          :model-value="selected.includes(date.format('YYYY-MM-DD'))"
          @update:model-value="toggleDate(date.format('YYYY-MM-DD'), $event)"
        /><FieldLabel :for="'att-' + date.format('YYYY-MM-DD')"
          ><span
            ><b class="tw:type-item tw:block tw:tracking-normal">{{
              date.locale(locale).format('dddd')
            }}</b
            ><small class="tw:type-secondary tw:text-(--muted-foreground) tw:block tw:mt-0.75">{{
              date.locale(locale).format(dateFormat)
            }}</small></span
          ></FieldLabel
        ></Field
      >
    </FieldGroup>
    <AppDialogNote v-if="step === ATTENDANCE_STEP.REASON" icon="alert" tone="danger">{{
      t('nkf.promptDetails.attendanceRisk')
    }}</AppDialogNote>
    <Field v-if="step === ATTENDANCE_STEP.REASON" class="attendance-field tw:mb-0.5"
      ><FieldLabel
        v-required-star
        class="attendance-label tw:type-label tw:ml-0.5"
        for="attendance-reason"
        >{{ t('nkf.interactions.attendanceReason') }}</FieldLabel
      ><Textarea
        id="attendance-reason"
        v-model="reason"
        required
        :aria-invalid="Boolean(errors.note)"
        :maxlength="FORM_LIMIT.NOTE"
        :placeholder="t('nkf.promptDetails.reasonPlaceholder')"
        class="tw:h-24"
        @update:model-value="clearErrors('note')"
    /></Field>
    <p v-for="(message, field) in errors" :key="field" class="field-error" role="alert">
      {{ message }}
    </p>
    <template #secondary-actions>
      <Button
        v-if="gap && step !== ATTENDANCE_STEP.DATES"
        :disabled="!dialysisCentre?.telephone"
        variant="outline"
        @click="callCareTeam"
        ><AppIcon size="action" name="phone" />{{ t('nkf.promptDetails.callCareTeam') }}</Button
      >
    </template>
    <template #footer>
      <div v-if="step === ATTENDANCE_STEP.QUESTION" class="button-pair">
        <Button
          variant="success"
          :disabled="isSubmitting"
          class="tw:gap-1.5 tw:px-2.5 tw:type-button"
          @click="answer(true)"
          ><AppIcon size="action" v-if="!gap" name="check" />{{
            t(gap ? 'nkf.promptDetails.went' : 'nkf.interactions.yes')
          }}</Button
        ><Button
          variant="outline"
          :disabled="isSubmitting"
          class="tw:text-(--result-high) tw:border-(--destructive-border) tw:gap-1.5 tw:px-2.5 tw:type-button"
          @click="answer(false)"
          ><AppIcon size="action" v-if="!gap" name="x" />{{
            t(gap ? 'nkf.promptDetails.notWent' : 'nkf.interactions.no')
          }}</Button
        >
      </div>
      <Button
        v-else-if="step === ATTENDANCE_STEP.DATES"
        :disabled="isSubmitting || !selected.length"
        @click="confirmDates"
        >{{ t('nkf.confirm') }}</Button
      >
      <Button v-else :disabled="isSubmitting || !reason.trim()" @click="submitReason">{{
        t('nkf.followup.submit')
      }}</Button>
    </template>
  </AppDialog>
</template>
<script setup>
// 1. Import necessary components
import { ref, shallowRef, computed } from 'vue';
import { useI18n, I18nT } from 'vue-i18n';
import { useRouter } from 'vue-router';
import dayjs from '@/core/boot/day';
import DateUtils from '@/shared/utilities/date-utils';
import { FORM_LIMIT } from '@/shared/constants/forms';
import { FLUID_NOTIFICATION } from '../constants/notification';
import { useFluidStore } from '@/core/stores/fluid.store';
import { useAuthStore } from '@/core/stores/auth.store';
import { useNotificationsStore } from '@/core/stores/notifications.store';
import fluidService from '@/shared/services/fluid.service';
import { attendanceSchema } from '@/validations/forms/attendance';
import { useValidate } from '@/shared/composables/useValidate';
import { DIALYSIS_ATTENDANCE_STATUS, FLUID_QUERY, READING_LOCATION } from '../constants/reading';
import { useToastService } from '@/shared/composables/useToastService';
import AppDialog from '@/shared/components/AppDialog.vue';
import AppDialogIllustration from '@/shared/components/AppDialogIllustration.vue';
import AppDialogNote from '@/shared/components/AppDialogNote.vue';
import AppIcon from '@/shared/components/AppIcon.vue';
import { Button } from '@/shared/components/ui/button';
import { useCallSheet } from '@/shared/composables/useCallSheet';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Textarea } from '@/shared/components/ui/textarea';
import { Field, FieldGroup, FieldLabel } from '@/shared/components/ui/field';
// 2. Use
const open = defineModel('open', { type: Boolean, default: false });
const props = defineProps({
  gap: { type: Boolean, default: false },
  context: { type: Object, required: true },
  notificationUnique: { type: String, default: undefined },
});
const { t, locale } = useI18n();
const router = useRouter();
const fluid = useFluidStore();
const auth = useAuthStore();
const inbox = useNotificationsStore();
const { validateData, errors, clearErrors } = useValidate();
const toast = useToastService();
const { callNumber } = useCallSheet();
// 3. Define a reactive variable
const ATTENDANCE_STEP = Object.freeze({ QUESTION: 'question', DATES: 'dates', REASON: 'reason' });
const step = shallowRef(ATTENDANCE_STEP.QUESTION);
const selected = ref([]);
const reason = shallowRef('');
const isSubmitting = shallowRef(false);
let responseRequest = null;
// 4. Define methods or computed properties if needed
const dialysisCentre = computed(() => auth.account.connectedFacilities);
const attendance = computed(() => props.context.dashboard.attendance);
const dates = computed(() => attendance.value.dates.map((date) => dayjs.utc(date)));
const dateFormat = computed(() => DateUtils.getClientDateTimeConfig().dayMonthShort);
const title = computed(() => {
  if (step.value === ATTENDANCE_STEP.DATES) {
    return t('nkf.interactions.attendanceDates');
  }
  if (step.value === ATTENDANCE_STEP.REASON) {
    return t('nkf.promptDetails.missingRisk');
  }
  if (props.gap) {
    return t('nkf.promptDetails.gapTitle');
  }
  return t('nkf.interactions.attendanceQuestion');
});
const description = computed(() => {
  if (step.value === ATTENDANCE_STEP.DATES) {
    return t('nkf.promptDetails.selectDays');
  }
  if (step.value === ATTENDANCE_STEP.REASON) {
    return '';
  }
  if (props.gap) {
    return t('nkf.promptDetails.gapDescription', { days: attendance.value.gapDays });
  }
  return t('nkf.promptDetails.attendanceDay', {
    day: dayjs.utc(attendance.value.today).locale(locale.value).format('dddd'),
  });
});
async function answer(attended) {
  if (!props.gap) {
    let status = DIALYSIS_ATTENDANCE_STATUS.NO;
    if (attended) {
      status = DIALYSIS_ATTENDANCE_STATUS.ATTENDED;
    }
    await save({ dates: [attendance.value.today], status });
    return;
  }
  if (attended) {
    step.value = ATTENDANCE_STEP.DATES;
  } else {
    step.value = ATTENDANCE_STEP.REASON;
  }
}
async function save(payload) {
  if (isSubmitting.value) {
    return;
  }
  let notificationCode = FLUID_NOTIFICATION.ATTENDANCE;
  if (props.gap) {
    notificationCode = FLUID_NOTIFICATION.OVERDUE;
  }
  const details = { ...payload, notificationCode, questionDate: attendance.value.today };
  if (!props.gap && props.notificationUnique) {
    details.notificationUnique = props.notificationUnique;
  }
  const key = JSON.stringify(details);
  if (responseRequest?.key !== key) {
    responseRequest = { key, requestId: crypto.randomUUID() };
  }
  const result = validateData(attendanceSchema, {
    ...details,
    requestId: responseRequest.requestId,
  });
  if (!result.isValid) {
    return;
  }
  isSubmitting.value = true;
  try {
    const saved = await fluidService.saveAttendance(result.data);
    fluid.applyDashboard(saved.dashboard);
    if (saved.alreadyAnswered) {
      toast.info(t('nkf.notificationDetails.alreadyAnswered'));
    } else {
      toast.success(t('nkf.fluidDetails.saved'));
    }
    open.value = false;
    if (
      !props.gap &&
      payload.status === DIALYSIS_ATTENDANCE_STATUS.ATTENDED &&
      !saved.alreadyAnswered
    ) {
      await router.push({
        path: '/fluid',
        query: { [FLUID_QUERY.ENTRY]: READING_LOCATION.DIALYSIS },
      });
    }
    await inbox.refreshCount();
  } finally {
    isSubmitting.value = false;
  }
}
// Confirms the days the patient went; only selected dates are recorded as attended.
function callCareTeam() {
  open.value = false;
  callNumber(dialysisCentre.value.name, dialysisCentre.value.telephone);
}
async function confirmDates() {
  await save({ dates: selected.value, status: DIALYSIS_ATTENDANCE_STATUS.ATTENDED });
}
async function submitReason() {
  await save({
    dates: [attendance.value.today],
    status: DIALYSIS_ATTENDANCE_STATUS.NO,
    note: reason.value.trim(),
  });
}
function toggleDate(date, checked) {
  if (checked) {
    selected.value.push(date);
  } else {
    selected.value = selected.value.filter((value) => value !== date);
  }
}
// 5. VueJS Hooks
</script>
<style scoped>
.attendance-day {
  left: 14%;
  top: 44%;
  width: 28%;
  height: 21%;
}
.attendance-title--compact {
  font: var(--type-dialog);
}
.attendance-dates {
  margin-block: -2px;
}
</style>
